<template>
  <div class="slate-interactive-meter slate-paywall">
    <!-- Paywall -->
    <div class="slate-interactive-meter__content">
      <div class="slate-interactive-meter__text-container">
        <h1 class="slate-interactive-meter__headline">
          Where is the Slate News Quiz leaderboard?
        </h1>
        <p class="slate-interactive-meter__text">
          The Slatester and Slate Plus member leaderboards are only for Slate
          Plus subscribers. Join today to access and compare scores. You’ll also
          get unlimited reading on Slate and ad-free listening on all of our
          podcasts.
        </p>
        <p>
          <a class="slate-button" :href="plusUrl" target="_parent">
            Subscribe
          </a>
        </p>
        <p class="slate-interactive-meter__sign-in">
          Already a member?
          <a
            class="slate-interactive-meter__link"
            :href="signInUrl"
            target="_parent"
          >
            Sign in here.
          </a>
        </p>
      </div>
      <div class="slate-interactive-meter__image-container">
        <img
          src="https://compote.slate.com/images/b2a66943-f56a-4992-a48f-2467fcda2e5d.png"
          srcset="
            https://compote.slate.com/images/b2a66943-f56a-4992-a48f-2467fcda2e5d.png?width=320   320w,
            https://compote.slate.com/images/b2a66943-f56a-4992-a48f-2467fcda2e5d.png?width=480   480w,
            https://compote.slate.com/images/b2a66943-f56a-4992-a48f-2467fcda2e5d.png?width=600   600w,
            https://compote.slate.com/images/b2a66943-f56a-4992-a48f-2467fcda2e5d.png?width=840   840w,
            https://compote.slate.com/images/b2a66943-f56a-4992-a48f-2467fcda2e5d.png?width=960   960w,
            https://compote.slate.com/images/b2a66943-f56a-4992-a48f-2467fcda2e5d.png?width=1280 1280w,
            https://compote.slate.com/images/b2a66943-f56a-4992-a48f-2467fcda2e5d.png?width=1440 1440w,
            https://compote.slate.com/images/b2a66943-f56a-4992-a48f-2467fcda2e5d.png?width=1600 1600w,
            https://compote.slate.com/images/b2a66943-f56a-4992-a48f-2467fcda2e5d.png?width=1920 1920w
          "
          sizes="
						(max-width: 450px) 320px,
						(max-width: 768px) 620px,
						(max-width: 1023px) 709px,
						546px
					"
          alt="Illustration of a person reading on laptop, and drinking from a coffee cup"
          width="276"
          height="298"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Roadblock",
  computed: {
    parentUrl() {
      return new URL(location.href).searchParams.get("parentUrl");
    },
    signInUrl() {
      const signInUrl = new URL("/sign-in", import.meta.env.VITE_SLATE_DOMAIN);
      if (this.parentUrl) {
        signInUrl.searchParams.set("redirect_uri", this.parentUrl);
      }
      return signInUrl.toString();
    },
    plusUrl() {
      const plusUrl = new URL("/plus", import.meta.env.VITE_SLATE_DOMAIN);
      plusUrl.search = new URLSearchParams({
        utm_medium: "link",
        utm_campaign: "plus",
        utm_content: "NewsQuiz",
        utm_source: "article",
        redirect_uri: this.parentUrl,
      });
      return plusUrl.href;
    },
  },
};
</script>

<style scoped lang="scss">
@import "../styles/fonts.scss";
:root {
  // override standard document bg color to display the fade
  background: transparent;
}

// Colors
// -------------------------
$raisin: #2c0022; // BASE
$clementine: #ffad10; // BASE\
$line-height-xs: 1;

@mixin button-padding($font-size: 1em) {
  padding: ($font-size/2) ($font-size/1.75);
}

// Because the interactive is in an iframe, our normal breakpoints don't work if the parent container is narrower than the viewport width.
// This variable is the min-width of the center column on the one-column layout on $bp-lg and greater breakpoints.
$content-column-bp: 635px;

$paywall-overflow-mobile: 33px;
$paywall-overflow-desktop: 23px;

.slate-interactive-meter {
  color: $raisin;
  position: relative;
  background: $clementine;
  font-family: $retina;
  padding: 20px 20px 0;
  margin-top: 1em;

  p {
    margin: 1rem 0;
  }

  &.slate-paywall,
  &.slate-roadblock {
    background: var(--bg-color);
    padding: 0 0 $paywall-overflow-mobile 0;

    ul {
      list-style: disc;
      padding-left: 20px;
    }

    li {
      margin-bottom: 0.5rem;
    }

    @media (min-width: $content-column-bp) {
      padding-bottom: $paywall-overflow-desktop;
    }
  }

  &__headline {
    font-family: $register;
    font-size: text-grow(7);
    line-height: $line-height-xs;
    margin-top: 0;
    margin-bottom: 0;
  }

  &__content {
    .slate-paywall &,
    .slate-roadblock & {
      background: $clementine;
      display: flex;
      flex-direction: column;
      grid-column: 2;
      padding: 20px 20px 0;

      @media (min-width: $content-column-bp) {
        flex-direction: row;
        grid-column: 3;
      }
    }
  }

  &__image-container {
    .slate-paywall & {
      bottom: -#{$paywall-overflow-mobile};
      flex-shrink: 0;
      flex-grow: 1;
      max-width: 325px;
      position: relative;

      align-self: flex-end;
      display: flex;
      justify-content: flex-end;

      img {
        width: 60%;
        margin-top: -12%;
        @media (min-width: $content-column-bp) {
          width: 85%;
          margin-top: 0;
        }
      }

      @media (min-width: $content-column-bp) {
        align-self: flex-end;
        bottom: -#{$paywall-overflow-desktop};
        right: -10px;
        justify-content: center;
      }
    }
  }

  &__sign-in {
    font-size: text-shrink(4);
  }

  &__link {
    text-decoration: underline;
  }

  &__text-container {
    flex-shrink: 1;
  }

  &__text {
    margin-top: 0.5rem;

    .slate-roadblock & {
      margin-top: 1rem;
    }
  }

  @media print {
    display: none;
  }
}
</style>
